import { useTranslation } from 'react-i18next';
import type { IActivityLocationGroup, IActivityProductivityRow } from '@interfaces/IProductivity';
import { ArrowUpRightIcon, CadIcon } from '@utils/icons';
import { formatNumber } from '@utils/formatNumber';
import { formatPercentCell } from '../../../../utils/formatPercent';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { ProfessionPill } from '@components/ui/ProfessionPill/ProfessionPill';
import { CardHeader } from '../../../../shared/CardHeader/CardHeader';
import { ConfidencePill } from '../../Table/ConfidencePill/ConfidencePill';
import { ProductivityBar } from '../../Table/ProductivityBar/ProductivityBar';
import { makeSortableTh } from '../../Table/Th/makeSortableTh';
import { NameCell } from '../../Table/NameCell';
import { BreakdownTableFooter } from '../../Table/Footer';
import { useSortableRows } from '../../Table/useSortableRows';
import { usePagination } from '../../Table/usePagination';
import table from '../../Table/breakdownTable.module.css';
import styles from './ActivityPanel.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface IActivityPanelProps {
  rows: IActivityProductivityRow[];
  onRowClick: (row: IActivityProductivityRow) => void;
}

/** Floors shown as inline chips before the rest collapse into a “+N” overflow
 *  chip — keeps the location cell one line tall no matter how many floors an
 *  activity touches. */
const MAX_VISIBLE_FLOORS = 2;

/** Location cell: a chip per floor the activity touches, labelled
 *  `floor (n | all)`, with that floor's exact zones on hover. Beyond
 *  MAX_VISIBLE_FLOORS the remaining floors fold into one “+N” chip that lists
 *  them on hover. Empty mapping renders an em-dash. */
function LocationsCell({ locations }: { locations: IActivityLocationGroup[] }) {
  const { t } = useTranslation();
  if (locations.length === 0) return <>—</>;

  const chipLabel = (group: IActivityLocationGroup) => {
    // A fully-covered single-zone floor needs no suffix — "(all)"
    if (group.allZonesOnFloor && group.zones.length === 1) return group.floor;
    const count = group.allZonesOnFloor
      ? t('productivity.activity.allZones')
      : String(group.zones.length);
    return `${group.floor} (${count})`;
  };

  const visible = locations.slice(0, MAX_VISIBLE_FLOORS);
  const overflow = locations.slice(MAX_VISIBLE_FLOORS);
  
  return (
    <span className={table.multiCell}>
      {visible.map((group) => (
        <Tooltip key={group.floor} content={group.zones.join(', ')}>
          <span className={table.floorChip}>
            <bdi>{chipLabel(group)}</bdi>
          </span>
        </Tooltip>
      ))}
      {overflow.length > 0 && (
        <Tooltip content={overflow.map(chipLabel).join(', ')}>
          <span className={table.floorChip}>+{overflow.length}</span>
        </Tooltip>
      )}
    </span>
  );
}

export function ActivityPanel({ rows, onRowClick }: IActivityPanelProps) {
  const { t } = useTranslation();
  const sort = useSortableRows(rows, { key: 'activity', dir: 'asc' });
  const pager = usePagination(sort.sorted, sort.sortSignature);
  const th = makeSortableTh('activity', sort, t);

  return (
    <>
      <CardHeader
        icon={<CadIcon size={16} />}
        title={t('productivity.activity.title')}
        meta={t('productivity.activity.meta')}
      />

      <ScrollArea orientation="horizontal">
        <table className={table.table}>
          <colgroup>
            <col className={styles.colActivity} />
            <col className={styles.colLocation} />
            <col className={styles.colTeam} />
            <col className={styles.colPlanned} />
            <col className={styles.colActual} />
            <col className={styles.colProductivity} />
            <col className={styles.colSpi} />
            <col className={styles.colConfidence} />
          </colgroup>
          <thead>
            <tr>
              {th('activity', 'productivity.activity.columns.activity')}
              <th className={table.th}>{t('productivity.activity.columns.location')}</th>
              <th className={table.th}>{t('productivity.activity.columns.profession')}</th>
              {th('plannedWorkDays', 'productivity.activity.columns.plannedWorkDays')}
              {th('actualWorkDays', 'productivity.activity.columns.actualWorkDays')}
              {th('productivity', 'productivity.activity.columns.productivity')}
              {th('spi', 'productivity.activity.columns.spi')}
              {th('confidence', 'productivity.activity.columns.confidence')}
            </tr>
          </thead>
          <tbody>
            {pager.visible.map((row) => (
              <tr key={row.id} className={`${table.tr} ${table.trAction} ${table.trLink}`} onClick={() => onRowClick(row)}>
                <NameCell value={row.activity} trailing={<ArrowUpRightIcon size={12} />} onActivate={() => onRowClick(row)} />
                <td className={table.td}>
                  <LocationsCell locations={row.locations} />
                </td>
                <td className={table.td}>
                  {row.profession ? (
                    <span className={table.teamText}>
                      <ProfessionPill profession={row.profession} showContractor compact truncate />
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                <td className={table.td}>{formatNumber(row.plannedWorkDays)}</td>
                <td className={table.td}>{formatNumber(row.actualWorkDays)}</td>
                <td className={table.td}>
                  <ProductivityBar value={row.productivity} />
                </td>
                <td className={table.td}>{formatPercentCell(row.spi)}</td>
                <td className={table.td}>
                  <ConfidencePill level={row.confidence} reason={row.confidenceReason} />
                </td>
              </tr>
            ))}
          </tbody>
          <BreakdownTableFooter pager={pager} />
        </table>
      </ScrollArea>
    </>
  );
}
